import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  PageHeader,
  PageSection,
  Select,
  Table,
} from '@/components';
import { useSearchParams } from 'react-router-dom';
import { useAnalyticsOverview, useMessageMetrics, useQueueStatus, useMessageList } from '@/hooks';
import type { AnalyticsChannel, AnalyticsRange } from '@/services/analyticsService';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts';

export function Analytics() {
  const [params, setParams] = useSearchParams();

  const range = (params.get('range') ?? '7d') as AnalyticsRange;
  const channel = (params.get('channel') ?? 'all') as AnalyticsChannel;

  const overview = useAnalyticsOverview({ range, channel });
  const metrics = useMessageMetrics({ range, channel });
  const queueStatus = useQueueStatus();
  const messageList = useMessageList({ range, channel });

  return (
    <Page>
      <PageHeader
        title="Analytics"
        description="Métricas operacionais (mensagens, entregas, opt-out e templates)."
        right={
          <div className="dashboard-filters">
            <Select
              value={range}
              onChange={(e) =>
                setParams((prev) => {
                  prev.set('range', e.target.value);
                  return prev;
                })
              }
              aria-label="Período"
            >
              <option value="today">Hoje</option>
              <option value="7d">Últimos 7 dias</option>
              <option value="30d">Últimos 30 dias</option>
            </Select>
            <Select
              value={channel}
              onChange={(e) =>
                setParams((prev) => {
                  prev.set('channel', e.target.value);
                  return prev;
                })
              }
              aria-label="Canal"
            >
              <option value="all">Todos</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="webchat">Web Chat</option>
            </Select>
          </div>
        }
      />

      <PageSection>
        {overview.isLoading ? (
          <LoadingState title="Carregando overview" />
        ) : overview.isError ? (
          <ErrorState title="Falha ao carregar overview" onRetry={() => overview.refetch()} />
        ) : !overview.data ? (
          <EmptyState title="Sem dados" description="Nenhum dado disponível para os filtros atuais." />
        ) : (
          <div className="stats-grid">
            <Card>
              <CardBody>
                <div className="text-sm text-gray-600">Mensagens enviadas</div>
                <div className="text-2xl font-bold text-gray-900">{overview.data.messages.sent.toLocaleString('pt-BR')}</div>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <div className="text-sm text-gray-600">Entregues</div>
                <div className="text-2xl font-bold text-gray-900">{overview.data.messages.delivered.toLocaleString('pt-BR')}</div>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <div className="text-sm text-gray-600">Falhas</div>
                <div className="text-2xl font-bold text-gray-900">{overview.data.messages.failed.toLocaleString('pt-BR')}</div>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <div className="text-sm text-gray-600">Delivery rate</div>
                <div className="text-2xl font-bold text-gray-900">{overview.data.messages.delivery_rate}%</div>
              </CardBody>
            </Card>
          </div>
        )}
      </PageSection>

      <PageSection>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="font-semibold">Fila de Mensagens (Tempo Real)</div>
              <Badge variant="neutral">Atualiza a cada 5s</Badge>
            </div>
          </CardHeader>
          <CardBody>
            {queueStatus.isLoading ? (
              <LoadingState title="Carregando fila" />
            ) : queueStatus.isError ? (
              <ErrorState title="Falha ao carregar fila" onRetry={() => queueStatus.refetch()} />
            ) : !queueStatus.data ? (
              <EmptyState title="Sem dados" description="Nenhuma mensagem na fila." />
            ) : (
              <div className="stats-grid">
                <Card>
                  <CardBody>
                    <div className="text-sm text-gray-600">Pendentes</div>
                    <div className="text-2xl font-bold text-yellow-600">{queueStatus.data.pending}</div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <div className="text-sm text-gray-600">Agendadas</div>
                    <div className="text-2xl font-bold text-blue-600">{queueStatus.data.scheduled}</div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <div className="text-sm text-gray-600">Processando</div>
                    <div className="text-2xl font-bold text-purple-600">{queueStatus.data.processing}</div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <div className="text-sm text-gray-600">Enviadas</div>
                    <div className="text-2xl font-bold text-green-600">{queueStatus.data.sent}</div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <div className="text-sm text-gray-600">Falhas</div>
                    <div className="text-2xl font-bold text-red-600">{queueStatus.data.failed}</div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <div className="text-sm text-gray-600">Total</div>
                    <div className="text-2xl font-bold text-gray-900">{queueStatus.data.total}</div>
                  </CardBody>
                </Card>
              </div>
            )}
          </CardBody>
        </Card>
      </PageSection>

      <PageSection>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="font-semibold">Timeline de mensagens</div>
              <Badge variant="neutral">{range} / {channel}</Badge>
            </div>
          </CardHeader>
          <CardBody>
            {metrics.isLoading ? (
              <LoadingState title="Carregando série" />
            ) : metrics.isError ? (
              <ErrorState title="Falha ao carregar série" onRetry={() => metrics.refetch()} />
            ) : !metrics.data?.timeline?.length ? (
              <EmptyState title="Sem timeline" description="Nenhum ponto na série para os filtros atuais." />
            ) : (
              <div style={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <LineChart data={metrics.data.timeline} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="sent" stroke="#111827" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="delivered" stroke="#16a34a" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="failed" stroke="#dc2626" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardBody>
        </Card>
      </PageSection>

      <PageSection>
        <Card>
          <CardHeader>
            <div className="font-semibold">Mensagens ({messageList.data?.length ?? 0})</div>
          </CardHeader>
          <CardBody>
            {messageList.isLoading ? (
              <LoadingState title="Carregando mensagens" />
            ) : messageList.isError ? (
              <ErrorState title="Falha ao carregar mensagens" onRetry={() => messageList.refetch()} />
            ) : !messageList.data?.length ? (
              <EmptyState title="Sem mensagens" description="Nenhuma mensagem no período selecionado." />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Evento</th>
                    <th>Telefone</th>
                    <th>Template</th>
                    <th>Tentativas</th>
                    <th>Status</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {messageList.data.map((m) => (
                    <tr key={m.id}>
                      <td className="font-medium">{m.id.slice(0, 8)}...</td>
                      <td>{m.event_type}</td>
                      <td>{m.recipient_phone ?? '-'}</td>
                      <td>{m.template_key ?? '-'}</td>
                      <td>{m.attempts ?? 0}/{m.max_attempts ?? 0}</td>
                      <td>
                        <Badge
                          variant={
                            m.status === 'sent'
                              ? 'success'
                              : m.status === 'failed'
                                ? 'error'
                                : m.status === 'pending' || m.status === 'processing'
                                  ? 'warning'
                                  : 'neutral'
                          }
                        >
                          {m.status}
                        </Badge>
                      </td>
                      <td>{new Date(m.created_at).toLocaleString('pt-BR')}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </PageSection>
    </Page>
  );
}
